import React, { useEffect, useState, useMemo } from "react";
import { useParams } from "react-router-dom";
import { getUserCreatedPlans } from "../../apis";

const formatDate = (value) => {
  const d = new Date(value);
  if (isNaN(d)) return { day: "--", month: "", weekday: "", full: value };
  return {
    day: d.toLocaleDateString("en-GB", { day: "2-digit" }),
    month: d.toLocaleDateString("en-GB", { month: "short", year: "numeric" }),
    weekday: d.toLocaleDateString("en-GB", { weekday: "long" }),
    full: d.toDateString(),
  };
};

const todayKey = () => new Date().toISOString().slice(0, 10);

const ItemCard = ({ item, onOpen }) => (
  <div className="up-card-item" onClick={() => onOpen(item.image)}>
    <div className="up-card-img">
      <img src={item.image} alt={item.name} loading="lazy" />
    </div>
    <div className="up-card-info">
      <div className="up-card-name" title={item.name}>{item.name}</div>
      <div className="up-tags">
        {item.category && <span className="up-tag">{item.category}</span>}
        {item.subcategory && <span className="up-tag light">{item.subcategory}</span>}
      </div>
    </div>
  </div>
);

const UserPlans = () => {
  const { id } = useParams();
  const [loading, setLoading] = useState(true);
  const [isAccess, setIsAccess] = useState(true);
  const [days, setDays] = useState([]);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [preview, setPreview] = useState(null);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        setLoading(true);
        const res = await getUserCreatedPlans(id);
        if (!active) return;
        setIsAccess(res?.data?.data?.isAccess !== false);
        setDays(res?.data?.data?.plans || []);
      } catch (e) {
        if (active) setDays([]);
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [id]);

  const visibleDays = useMemo(() => {
    const today = todayKey();
    const term = search.trim().toLowerCase();
    return days
      .filter((d) => {
        if (filter === "upcoming") return d.date >= today;
        if (filter === "past") return d.date < today;
        return true;
      })
      .map((d) => ({
        ...d,
        plans: d.plans.filter(
          (p) =>
            !term ||
            (p.name || "").toLowerCase().includes(term) ||
            p.items.some((i) => (i.name || "").toLowerCase().includes(term)) ||
            p.outfits.some((o) => (o.name || "").toLowerCase().includes(term))
        ),
      }))
      .filter((d) => d.plans.length);
  }, [days, filter, search]);

  const totalOutfits = days.reduce((n, d) => n + d.plans.reduce((m, p) => m + p.outfits.length, 0), 0);
  const totalItems = days.reduce((n, d) => n + d.plans.reduce((m, p) => m + p.items.length, 0), 0);

  if (loading) return <div className="up-empty">Loading plans…</div>;
  if (!isAccess)
    return <div className="up-empty">You don't have access to this user's plans.</div>;

  return (
    <div className="up-wrap">
      <div className="up-header">
        <div>
          <h2>User Plans</h2>
          <p>Outfits and items this user has planned for specific dates or occasions.</p>
        </div>
        <div className="up-controls">
          <div className="up-chips">
            {["all", "upcoming", "past"].map((f) => (
              <button
                key={f}
                className={filter === f ? "active" : ""}
                onClick={() => setFilter(f)}
              >
                {f[0].toUpperCase() + f.slice(1)}
              </button>
            ))}
          </div>
          <input
            type="text"
            placeholder="Search occasion, item or outfit..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      </div>

      <div className="up-stats">
        <div><strong>{days.length}</strong><span>Planned days</span></div>
        <div><strong>{totalOutfits}</strong><span>Outfits planned</span></div>
        <div><strong>{totalItems}</strong><span>Items planned</span></div>
      </div>

      {visibleDays.length === 0 ? (
        <div className="up-empty">No plans found.</div>
      ) : (
        visibleDays.map((d) => {
          const f = formatDate(d.date);
          const isToday = d.date === todayKey();
          const isPast = d.date < todayKey();
          return (
            <section className="up-day" key={d.date}>
              <div className="up-day-head">
                <div className="up-day-title">
                  <span className="up-day-date">{f.weekday}, {f.day} {f.month}</span>
                  {isToday && <span className="up-badge dark">Today</span>}
                  {!isToday && isPast && <span className="up-badge">Past</span>}
                  {!isToday && !isPast && <span className="up-badge green">Upcoming</span>}
                </div>
              </div>

              {d.plans.map((p, idx) => (
                <div className="up-plan" key={idx}>
                  <div className="up-plan-head">
                    <span className="up-occasion">{p.name || "Untitled plan"}</span>
                  </div>

                  {p.outfits.length > 0 && (
                    <>
                      <div className="up-label">Outfits ({p.outfits.length})</div>
                      <div className="up-outfits">
                        {p.outfits.map((o) => (
                          <div className="up-outfit" key={o._id}>
                            <div className="up-outfit-cover" onClick={() => setPreview(o.image)}>
                              <img src={o.image} alt={o.name} loading="lazy" />
                            </div>
                            <div className="up-outfit-body">
                              <div className="up-card-name" title={o.name}>{o.name}</div>
                              {o.description && <div className="up-outfit-desc">{o.description}</div>}
                              {o.items.length > 0 && (
                                <div className="up-mini">
                                  {o.items.map((i) => (
                                    <img key={i._id} src={i.image} alt={i.name} title={i.name} onClick={() => setPreview(i.image)} />
                                  ))}
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </>
                  )}

                  {p.items.length > 0 && (
                    <>
                      <div className="up-label">Items ({p.items.length})</div>
                      <div className="up-grid">
                        {p.items.map((i) => (
                          <ItemCard key={i._id} item={i} onOpen={setPreview} />
                        ))}
                      </div>
                    </>
                  )}
                </div>
              ))}
            </section>
          );
        })
      )}

      {preview && (
        <div className="up-modal" onClick={() => setPreview(null)}>
          <img src={preview} alt="preview" />
        </div>
      )}
    </div>
  );
};

export default UserPlans;
